import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";
import { confirmCode, friendlyError, sendCode } from "@/lib/firebasePhone";
import { Loader2 } from "lucide-react";

const RESEND_SECONDS = 30;

/**
 * Mobile number → SMS code → (first time: name + email) → signed in.
 * onSignedIn({token, user, new_account}) is called with our backend session.
 */
export default function PhoneLogin({ onSignedIn }) {
  return (
    <>
      <PhoneLoginSteps onSignedIn={onSignedIn} />
      {/* Invisible reCAPTCHA that Firebase requires before sending an SMS. */}
      <div id="phone-login-recaptcha" />
    </>
  );
}

function PhoneLoginSteps({ onSignedIn }) {
  const [step, setStep] = useState("phone");   // phone | code | profile
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [idToken, setIdToken] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [wait, setWait] = useState(0);

  useEffect(() => {
    if (wait <= 0) return undefined;
    const t = setTimeout(() => setWait(w => w - 1), 1000);
    return () => clearTimeout(t);
  }, [wait]);

  const send = async (e) => {
    e?.preventDefault();
    if (!/^[6-9]\d{9}$/.test(phone)) return setError("Enter your 10-digit mobile number");
    setError(""); setBusy(true);
    try {
      setConfirmation(await sendCode(phone, "phone-login-recaptcha"));
      setStep("code"); setCode(""); setWait(RESEND_SECONDS);
    } catch (err) { setError(friendlyError(err)); }
    finally { setBusy(false); }
  };

  const finish = async (token, profile = {}) => {
    const { data } = await api.post("/auth/phone/login", { id_token: token, ...profile });
    if (data.needs_profile) { setIdToken(token); setStep("profile"); return; }
    await onSignedIn(data);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return setError("Enter the 6-digit code");
    setError(""); setBusy(true);
    try {
      let token;
      try { token = await confirmCode(confirmation, code); }
      catch (err) { setError(friendlyError(err)); return; }
      await finish(token);
    } catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setBusy(false); }
  };

  const createAccount = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return setError("Enter your name");
    setError(""); setBusy(true);
    try { await finish(idToken, { name: name.trim(), email: email.trim() }); }
    catch (err) {
      const d = err.response?.data?.detail;
      // The Firebase token lasts an hour; after that start again.
      if (err.response?.status === 401) { setStep("phone"); setIdToken(null); }
      setError(formatApiErrorDetail(d) || err.message);
    } finally { setBusy(false); }
  };

  const errorBox = error && (
    <div data-testid="phone-login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{error}</div>
  );

  if (step === "profile") {
    return (
      <form onSubmit={createAccount} className="space-y-4" data-testid="phone-login-profile">
        <div className="text-sm text-stone-600">Welcome to PawBazaar! <b>+91 {phone}</b> is confirmed. Tell us who you are.</div>
        <div><Label htmlFor="pl-name">Your name</Label>
          <Input id="pl-name" value={name} onChange={e => setName(e.target.value)} maxLength={100} className="h-12 mt-1" data-testid="phone-login-name" autoFocus /></div>
        <div><Label htmlFor="pl-email">Email</Label>
          <Input id="pl-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} className="h-12 mt-1" data-testid="phone-login-email" />
          <p className="text-xs text-stone-500 mt-1">We send order updates and receipts here.</p></div>
        {errorBox}
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold" data-testid="phone-login-create">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
    );
  }

  if (step === "code") {
    return (
      <form onSubmit={verify} className="space-y-4" data-testid="phone-login-code-step">
        <div className="text-sm text-stone-600">
          We sent a 6-digit code to <b>+91 {phone}</b>.{" "}
          <button type="button" className="text-teal hover:underline" onClick={() => { setStep("phone"); setError(""); }}>Change number</button>
        </div>
        <div><Label htmlFor="pl-code">Code</Label>
          <Input id="pl-code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="h-12 mt-1 font-mono tracking-[0.4em] text-lg" data-testid="phone-login-code" autoFocus /></div>
        {errorBox}
        <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold" data-testid="phone-login-verify">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify and sign in"}
        </Button>
        <div className="text-center text-xs text-stone-500">
          {wait > 0 ? `Resend code in ${wait}s` : (
            <button type="button" className="text-teal hover:underline" onClick={send} disabled={busy}>Resend code</button>
          )}
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={send} className="space-y-4" data-testid="phone-login-phone-step">
      <div>
        <Label htmlFor="pl-phone">Mobile number</Label>
        <div className="flex mt-1">
          <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-stone-50 text-sm text-stone-600">+91</span>
          <Input id="pl-phone" inputMode="numeric" autoComplete="tel-national" maxLength={10} value={phone}
            onChange={e => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
            className="h-12 rounded-l-none" placeholder="10-digit number" data-testid="phone-login-phone" />
        </div>
      </div>
      <p className="text-xs text-stone-500 -mt-2">We send a 6-digit code by SMS. No password needed.</p>
      {errorBox}
      <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-stoneWarm text-white hover:bg-stoneWarm/90 font-semibold" data-testid="phone-login-send">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send code"}
      </Button>
      <p className="text-[11px] text-stone-500 text-center">New to PawBazaar? This creates your account. SMS rates may apply.</p>
    </form>
  );
}
